/**
 * Tooltip — lightweight hover-tooltip primitive for inline labels.
 *
 * A controlled atom built on React state + Tailwind positioning (no new
 * Radix dependency). Wraps a child trigger element and renders the tooltip
 * label above (or below) the trigger on hover/focus.
 *
 * Why a custom atom (not `@radix-ui/react-tooltip`):
 *   - The codebase already standardises on a small Radix surface
 *     (`react-dialog`, `react-select`, etc.). Adding a new Radix package
 *     just to surface a per-row date stamp would inflate the bundle and
 *     introduce a portal/provider that isn't needed elsewhere yet. The CSS
 *     hover-tooltip is sufficient for the People DB activity feed and the
 *     handful of other inline-label cases we have today (LEV-766).
 *
 * Behaviour:
 *   - The trigger is rendered via a `<span class="relative inline-flex">`.
 *     Hovering/focusing the trigger reveals the tooltip via Tailwind's
 *     `group-hover` / `group-focus-within` modifiers, so no JS state churn
 *     is required.
 *   - Opt-ins (default off, so every existing caller renders unchanged):
 *     `focusVisibleOnly` reveals on keyboard focus only
 *     (`group-has-[:focus-visible]`), so a mouse click that focuses the
 *     trigger doesn't pin the bubble open; `delayedHover` delays the hover
 *     reveal by 500ms so a passing cursor doesn't flash it.
 *   - `position="top"` (default) renders the bubble above the trigger.
 *     `position="bottom"` renders below. `align="center"` (default) centers
 *     the bubble on the trigger; `align="end"` anchors its right edge to the
 *     trigger's right edge, for triggers near a container's right edge where
 *     a centered bubble would clip.
 *   - `multiline` switches the bubble from a single `whitespace-nowrap` line
 *     to a fixed-width (`w-64`) wrapping block, for longer hint copy.
 *   - `portal` renders the bubble into `document.body` as a `fixed` box placed
 *     from the trigger's rect and clamped inside the viewport, for triggers
 *     inside an `overflow-hidden` container (the evaluation panel header)
 *     where an absolute bubble would be clipped. Reveal is JS state (hover or
 *     any focus inside the trigger), since `group-*` classes cannot reach a
 *     portalled node; `focusVisibleOnly`, `delayedHover` and
 *     `hideWhenExpanded` do not apply. `bubbleClassName` styles the bubble
 *     itself (e.g. a wider width).
 *   - `hideWhenExpanded` hides the bubble while a descendant of the trigger
 *     has `aria-expanded="true"` (e.g. a menu button mid-open) via
 *     `group-has-[[aria-expanded=true]]/tooltip:hidden` — an open menu
 *     panel would otherwise sit under a stale hint bubble.
 *
 * Accessibility:
 *   - The label content is rendered inside the DOM (not just `title=`) so
 *     screen readers announce it when the trigger gets focus. We also set
 *     `role="tooltip"` and link it to the trigger via `cloneElement`.
 *   - By default (`describeVia="aria"`) the child always gets
 *     `aria-describedby`. With `describeVia="prop"`, a native DOM tag
 *     (`<button>`, `<span>`, ...) still gets `aria-describedby`, but a
 *     composite component (e.g. `AddPipelineMenu`, `PropertiesMenu`), which
 *     can't forward an unrecognized `aria-describedby` to its own internal
 *     trigger button, gets `triggerDescribedBy` instead: a prop the
 *     component must explicitly accept and wire onto its real button.
 *
 * Usage:
 *   <Tooltip label="May 23, 2026, 9:42 AM">
 *     <span>2 hours ago</span>
 *   </Tooltip>
 */
"use client";

import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

export interface TooltipProps {
  /** The label rendered inside the tooltip bubble. */
  label: React.ReactNode;
  /** The trigger element. The tooltip anchors to this element. */
  children: React.ReactNode;
  /** Bubble placement relative to the trigger. Defaults to "top". */
  position?: "top" | "bottom";
  /**
   * Horizontal anchor relative to the trigger. "center" (default) centers
   * the bubble; "end" anchors its right edge to the trigger's right edge so
   * it doesn't clip past a container edge.
   */
  align?: "center" | "end";
  /** Additional class names applied to the wrapping span. */
  className?: string;
  /**
   * Wraps the bubble to a fixed width instead of a single `nowrap` line, for
   * longer hint copy.
   */
  multiline?: boolean;
  /** Overrides the generated tooltip id (and the trigger's aria-describedby). */
  id?: string;
  /**
   * Hides the bubble while a descendant of the trigger has
   * `aria-expanded="true"` (e.g. a menu button that is currently open).
   */
  hideWhenExpanded?: boolean;
  /**
   * Reveal on keyboard focus (`:focus-visible`) only, not on any focus, so a
   * mouse click that focuses the trigger doesn't pin the bubble open.
   */
  focusVisibleOnly?: boolean;
  /** Delays the hover reveal by 500ms so a passing cursor doesn't flash it. */
  delayedHover?: boolean;
  /**
   * How the trigger is linked to the bubble. "aria" (default) always injects
   * `aria-describedby`; "prop" injects `triggerDescribedBy` on a composite
   * component child (native DOM children still get `aria-describedby`).
   */
  describeVia?: "aria" | "prop";
  /** Render the bubble in a body portal, clamped to the viewport (see header). */
  portal?: boolean;
  /** Extra classes for the bubble itself, merged last (e.g. a wider `w-80`). */
  bubbleClassName?: string;
}

/** Space between the trigger and a portalled bubble, in px. */
const PORTAL_GAP = 4;
/** Minimum gap between a portalled bubble and the viewport edge, in px. */
const PORTAL_VIEWPORT_MARGIN = 8;

export function Tooltip({
  label,
  children,
  position = "top",
  align = "center",
  className,
  multiline,
  id,
  hideWhenExpanded,
  focusVisibleOnly,
  delayedHover,
  describeVia = "aria",
  portal = false,
  bubbleClassName,
}: TooltipProps) {
  const generatedId = React.useId();
  const tooltipId = id ?? generatedId;

  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const bubbleRef = React.useRef<HTMLSpanElement>(null);
  const [portalReady, setPortalReady] = React.useState(false);
  const [portalOpen, setPortalOpen] = React.useState(false);
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  React.useEffect(() => {
    if (portal) setPortalReady(true);
  }, [portal]);
  const place = React.useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;
    const rect = trigger.getBoundingClientRect();
    const width = bubble.offsetWidth;
    const wanted =
      align === "end" ? rect.right - width : rect.left + rect.width / 2 - width / 2;
    const maxLeft = Math.max(
      PORTAL_VIEWPORT_MARGIN,
      window.innerWidth - PORTAL_VIEWPORT_MARGIN - width,
    );
    // Vertical: the requested side, else the other side when only that fits,
    // else clamped inside the margins; taller than the viewport pins to the top.
    const height = bubble.offsetHeight;
    const below = rect.bottom + PORTAL_GAP;
    const above = rect.top - PORTAL_GAP - height;
    const maxTop = window.innerHeight - PORTAL_VIEWPORT_MARGIN - height;
    const fits = (t: number) => t >= PORTAL_VIEWPORT_MARGIN && t <= maxTop;
    const [first, second] = position === "bottom" ? [below, above] : [above, below];
    const top =
      maxTop < PORTAL_VIEWPORT_MARGIN
        ? PORTAL_VIEWPORT_MARGIN
        : fits(first)
          ? first
          : fits(second)
            ? second
            : Math.min(Math.max(first, PORTAL_VIEWPORT_MARGIN), maxTop);
    setCoords({
      left: Math.min(Math.max(wanted, PORTAL_VIEWPORT_MARGIN), maxLeft),
      top,
    });
  }, [align, position]);
  React.useLayoutEffect(() => {
    if (!portal || !portalOpen) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    // The bubble's own size changes (label swap, font load) move its clamp.
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && bubbleRef.current
        ? new ResizeObserver(place)
        : null;
    if (resizeObserver && bubbleRef.current) resizeObserver.observe(bubbleRef.current);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [portal, portalOpen, place]);

  // Four full literal strings (never a runtime concatenation) so Tailwind's
  // static scanner finds every class regardless of which branch is picked.
  const positionClass =
    position === "top"
      ? align === "end"
        ? "bottom-full mb-1 right-0"
        : "bottom-full mb-1 left-1/2 -translate-x-1/2"
      : align === "end"
        ? "top-full mt-1 right-0"
        : "top-full mt-1 left-1/2 -translate-x-1/2";

  // The default literal is the bubble every non-opt-in caller has always
  // rendered; changing it changes every Tooltip in the app. `multiline`
  // relies on `cn`'s tailwind-merge dropping this string's `whitespace-nowrap`.
  const bubbleClass = focusVisibleOnly
    ? "pointer-events-none absolute z-tooltip whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-md opacity-0 transition-opacity duration-150 group-hover/tooltip:opacity-100 group-has-[:focus-visible]/tooltip:opacity-100"
    : "pointer-events-none absolute z-tooltip whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-md opacity-0 transition-opacity duration-150 group-hover/tooltip:opacity-100 group-focus-within/tooltip:opacity-100";

  // Child-type branch documented above the component: under "prop", a
  // composite component needs `triggerDescribedBy` and must wire it onto its
  // own internal trigger button.
  const isNativeElement =
    React.isValidElement(children) && typeof children.type === "string";
  const describedByProp =
    describeVia === "prop" && !isNativeElement
      ? { triggerDescribedBy: tooltipId }
      : { "aria-describedby": tooltipId };

  const trigger = React.isValidElement(children)
    ? React.cloneElement(
        children as React.ReactElement<{
          "aria-describedby"?: string;
          triggerDescribedBy?: string;
          tabIndex?: number;
        }>,
        describedByProp,
      )
    : children;

  if (portal) {
    const shown = portalOpen && coords !== null;
    return (
      <span
        ref={triggerRef}
        className={cn("relative inline-flex items-center", className)}
        onMouseEnter={() => setPortalOpen(true)}
        onMouseLeave={() => setPortalOpen(false)}
        onFocus={() => setPortalOpen(true)}
        onBlur={() => setPortalOpen(false)}
      >
        {trigger}
        {portalReady &&
          createPortal(
            <span
              ref={bubbleRef}
              id={tooltipId}
              role="tooltip"
              className={cn(
                "pointer-events-none fixed z-tooltip whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-md transition-opacity duration-150",
                multiline && "whitespace-normal w-64 text-left leading-snug",
                bubbleClassName,
              )}
              style={{
                top: coords?.top ?? -9999,
                left: coords?.left ?? -9999,
                opacity: shown ? 1 : 0,
              }}
            >
              {label}
            </span>,
            document.body,
          )}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "group/tooltip relative inline-flex items-center",
        className,
      )}
    >
      {trigger}
      <span
        id={tooltipId}
        role="tooltip"
        className={cn(
          bubbleClass,
          positionClass,
          multiline && "whitespace-normal w-64 text-left leading-snug",
          delayedHover && "group-hover/tooltip:delay-500",
          hideWhenExpanded && "group-has-[[aria-expanded=true]]/tooltip:hidden",
        )}
      >
        {label}
      </span>
    </span>
  );
}
